import { News } from "@/types";

/**
 * Unified Image Utilities
 * Consolidates all image handling logic into a single, reliable system
 * Replaces both newsUtils.ts and imageUtils.ts image functions
 */

interface ImageConfig {
  baseUrl: string;
  cdnUrl?: string;
  fallbackImage: string;
  storagePrefix: string;
}

/**
 * Get environment-aware configuration
 */
const getImageConfig = (): ImageConfig => {
  const isDevelopment =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : import.meta.env.VITE_APP_URL || "http://localhost:8001";

  return {
    baseUrl,
    cdnUrl: import.meta.env.VITE_CDN_URL, // Optional CDN support
    fallbackImage: "/img/noimg.jpg",
    storagePrefix: "/storage/images/",
  };
};

/**
 * Validate if a string is a valid HTTP/HTTPS URL
 */
const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Clean and normalize image path
 */
const normalizeImagePath = (imagePath: string): string => {
  if (!imagePath) return "";

  // Remove leading/trailing whitespace
  imagePath = imagePath.trim();

  // Fix common URL malformations
  if (imagePath.includes("http:/localhost")) {
    imagePath = imagePath.replace("http:/localhost", "http://localhost");
  }
  if (imagePath.includes("https:/localhost")) {
    imagePath = imagePath.replace("https:/localhost", "https://localhost");
  }

  // Remove duplicate slashes (but preserve protocol slashes)
  imagePath = imagePath.replace(/([^:]\/)\/+/g, "$1");

  return imagePath;
};

/**
 * Main function to get image URL with comprehensive fallback strategy
 */
export const getImageUrl = (
  newsOrImageData: News | { image?: string; image_url?: string } | string,
): string => {
  const config = getImageConfig();

  // Handle string input (direct image path/URL)
  if (typeof newsOrImageData === "string") {
    const imagePath = normalizeImagePath(newsOrImageData);

    if (!imagePath) {
      return `${config.baseUrl}${config.fallbackImage}`;
    }

    // If it's already a full URL, return as-is
    if (isValidUrl(imagePath)) {
      return imagePath;
    }

    // Assume it's a storage path
    return `${config.baseUrl}${config.storagePrefix}${imagePath}`;
  }

  // Handle object input (News or image data)
  const imageData = newsOrImageData as
    | News
    | { image?: string; image_url?: string };

  // Priority 1: External image_url (full URL)
  if (imageData.image_url) {
    const imageUrl = normalizeImagePath(imageData.image_url);

    if (isValidUrl(imageUrl)) {
      return imageUrl;
    }

    // If image_url is not a full URL, treat it as a storage path
    if (imageUrl) {
      // Prevent URL duplication
      if (imageUrl.startsWith(config.baseUrl)) {
        return imageUrl;
      }
      return `${config.baseUrl}${config.storagePrefix}${imageUrl}`;
    }
  }

  // Priority 2: Local image in storage
  if (imageData.image) {
    const imagePath = normalizeImagePath(imageData.image);

    if (imagePath) {
      // Handle case where image field contains full URL
      if (isValidUrl(imagePath)) {
        return imagePath;
      }

      // Prevent URL duplication
      if (imagePath.startsWith(config.baseUrl)) {
        return imagePath;
      }

      // Assume it's a storage path
      return `${config.baseUrl}${config.storagePrefix}${imagePath}`;
    }
  }

  // Priority 3: Fallback image
  return `${config.baseUrl}${config.fallbackImage}`;
};

/**
 * Get optimized image URL with size parameters for local images
 */
export const getOptimizedImageUrl = (
  newsOrImageData: News | { image?: string; image_url?: string } | string,
  width?: number,
  height?: number,
  fit: "crop" | "contain" | "cover" = "crop",
  quality: number = 85,
): string => {
  const baseImageUrl = getImageUrl(newsOrImageData);

  // Only add optimization parameters for local images (not external URLs or fallback)
  if (baseImageUrl.includes("/storage/images/") && (width || height)) {
    const params = new URLSearchParams();

    if (width) params.set("w", width.toString());
    if (height) params.set("h", height.toString());
    params.set("fit", fit);
    params.set("q", quality.toString());

    return `${baseImageUrl}?${params.toString()}`;
  }

  return baseImageUrl;
};

/**
 * Generate responsive srcSet for images
 */
export const generateResponsiveSrcSet = (
  newsOrImageData: News | { image?: string; image_url?: string } | string,
  sizes: number[] = [320, 640, 768, 1024, 1280],
  fit: "crop" | "contain" | "cover" = "crop",
): string => {
  return sizes
    .map(
      (size) =>
        `${getOptimizedImageUrl(newsOrImageData, size, undefined, fit)} ${size}w`,
    )
    .join(", ");
};

/**
 * Generate sizes attribute for responsive images
 */
export const generateSizesAttribute = (): string => {
  return "(max-width: 320px) 320px, (max-width: 640px) 640px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1280px";
};

/**
 * Get avatar URL for users with fallback
 */
export const getAvatarUrl = (user: { avatar?: string } | null): string => {
  const config = getImageConfig();

  if (user?.avatar) {
    const avatarPath = normalizeImagePath(user.avatar);

    if (avatarPath) {
      if (isValidUrl(avatarPath)) {
        return avatarPath;
      }
      return `${config.baseUrl}${config.storagePrefix}${avatarPath}`;
    }
  }

  return `${config.baseUrl}/img/Default.png`;
};

/**
 * Get celebrity photo URL with fallback
 */
export const getCelebrityPhotoUrl = (
  celebrity: { photo?: string } | null,
): string => {
  const config = getImageConfig();

  if (celebrity?.photo) {
    const photoPath = normalizeImagePath(celebrity.photo);

    if (photoPath) {
      if (isValidUrl(photoPath)) {
        return photoPath;
      }
      return `${config.baseUrl}${config.storagePrefix}${photoPath}`;
    }
  }

  return `${config.baseUrl}/img/Default.png`;
};

/**
 * Get ads/banner URL
 */
export const getAdsUrl = (ads: string): string => {
  const config = getImageConfig();

  if (ads) {
    const adsPath = normalizeImagePath(ads);

    if (isValidUrl(adsPath)) {
      return adsPath;
    }

    return `${config.baseUrl}/img/banner/${adsPath}`;
  }

  return `${config.baseUrl}${config.fallbackImage}`;
};

/**
 * Validate if an image URL is accessible (with caching)
 */
const imageValidationCache = new Map<
  string,
  { isValid: boolean; timestamp: number }
>();
const VALIDATION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const validateImageUrl = async (url: string): Promise<boolean> => {
  // Check cache first
  const cached = imageValidationCache.get(url);
  if (cached && Date.now() - cached.timestamp < VALIDATION_CACHE_TTL) {
    return cached.isValid;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

    const response = await fetch(url, {
      method: "HEAD",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const isValid = response.ok;

    // Cache the result
    imageValidationCache.set(url, { isValid, timestamp: Date.now() });

    return isValid;
  } catch {
    // Cache negative result
    imageValidationCache.set(url, { isValid: false, timestamp: Date.now() });
    return false;
  }
};

/**
 * Preload image utility
 */
export const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

/**
 * Batch preload multiple images with concurrency control
 */
export const preloadImages = async (
  urls: string[],
  maxConcurrent: number = 3,
): Promise<void> => {
  const chunks: string[][] = [];

  // Split URLs into chunks for concurrent processing
  for (let i = 0; i < urls.length; i += maxConcurrent) {
    chunks.push(urls.slice(i, i + maxConcurrent));
  }

  try {
    for (const chunk of chunks) {
      await Promise.allSettled(chunk.map((url) => preloadImage(url)));
    }
  } catch (error) {
    console.warn("Some images failed to preload:", error);
  }
};

/**
 * Get image dimensions if accessible
 */
export const getImageDimensions = (
  url: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () =>
      reject(new Error(`Failed to load image for dimensions: ${url}`));
    img.src = url;
  });
};

/**
 * Clear image validation cache (useful for testing or manual refresh)
 */
export const clearImageCache = (): void => {
  imageValidationCache.clear();
};

/**
 * Get cache statistics for debugging
 */
export const getImageCacheStats = (): {
  size: number;
  entries: Array<{ url: string; isValid: boolean; age: number }>;
} => {
  const entries = Array.from(imageValidationCache.entries()).map(
    ([url, { isValid, timestamp }]) => ({
      url,
      isValid,
      age: Date.now() - timestamp,
    }),
  );

  return {
    size: imageValidationCache.size,
    entries,
  };
};

/**
 * Common image size presets for optimization
 */
export const IMAGE_SIZES = {
  thumbnail: { width: 150, height: 100 },
  small: { width: 300, height: 200 },
  medium: { width: 600, height: 400 },
  large: { width: 1200, height: 800 },
  hero: { width: 1920, height: 1080 },
} as const;

/**
 * Helper function to get common image sizes
 */
export const getImageSize = (size: keyof typeof IMAGE_SIZES) =>
  IMAGE_SIZES[size];

// Export default as main function for backward compatibility
export default getImageUrl;
